import { useState } from "react";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ defaultService = "", compact = false, source = "site" }) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const formData = new FormData(e.target);
    const payload = {
      service: formData.get("service") || "Non précisé",
      firstname: formData.get("firstname") || "",
      lastname: formData.get("lastname") || "",
      email: formData.get("email") || "",
      phone: formData.get("phone") || "",
      message: formData.get("message") || "",
      source: source,
      botcheck: formData.get("botcheck") || "",
    };

    // Mailto fallback if backend is unreachable (network error only)
    const openMailtoFallback = () => {
      const subject = encodeURIComponent(`Demande de devis — ${payload.service}`);
      const body = encodeURIComponent(
        `Service: ${payload.service}\n` +
        `Nom: ${payload.firstname} ${payload.lastname}\n` +
        `Email: ${payload.email}\n` +
        `Téléphone: ${payload.phone}\n\n` +
        `Message:\n${payload.message || "-"}`
      );
      window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    };

    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStatus("success");
        e.target.reset();
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMsg(data.detail || "L'envoi a échoué. Réessayez ou appelez-nous directement.");
      }
    } catch (err) {
      // Network failure — mailto fallback as last resort
      openMailtoFallback();
      setStatus("success");
      e.target.reset();
    }
  };

  if (status === "success") {
    return (
      <div data-testid="form-success" className="text-center py-10 px-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-terracotta/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-8 h-8 text-terracotta" />
        </div>
        <h3 className="font-display text-2xl text-forest mb-3">Merci pour votre demande !</h3>
        <p className="text-forest/70 max-w-md mx-auto">
          Nous vous recontactons dans les plus brefs délais avec une solution adaptée. Pour une réponse immédiate, appelez-nous au{" "}
          <a href={`tel:${COMPANY.phone}`} className="text-terracotta font-medium underline">{COMPANY.phoneDisplay}</a>.
        </p>
        <button
          onClick={() => setStatus("idle")}
          data-testid="form-success-reset"
          className="mt-6 text-sm text-forest/60 hover:text-terracotta underline underline-offset-4"
        >
          Envoyer une nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-forest mb-2" htmlFor="service">
          Quel est votre besoin ?
        </label>
        <select
          id="service"
          name="service"
          defaultValue={defaultService}
          data-testid="form-service"
          className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
        >
          <option value="">— Sélectionnez un service —</option>
          {SERVICES.map(s => (
            <option key={s.slug} value={s.title}>{s.title}</option>
          ))}
          <option value="Autre">Autre / Plusieurs besoins</option>
        </select>
      </div>

      <div className={compact ? "" : "grid grid-cols-1 md:grid-cols-2 gap-5"}>
        <div>
          <label className="block text-sm font-medium text-forest mb-2" htmlFor="firstname">Prénom *</label>
          <input
            id="firstname"
            type="text"
            name="firstname"
            required
            data-testid="form-firstname"
            className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-forest mb-2" htmlFor="lastname">Nom *</label>
          <input
            id="lastname"
            type="text"
            name="lastname"
            required
            data-testid="form-lastname"
            className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
          />
        </div>
      </div>

      <div className={compact ? "" : "grid grid-cols-1 md:grid-cols-2 gap-5"}>
        <div>
          <label className="block text-sm font-medium text-forest mb-2" htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            name="email"
            required
            data-testid="form-email"
            className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-forest mb-2" htmlFor="phone">Téléphone *</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            required
            data-testid="form-phone"
            className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all"
          />
        </div>
      </div>

      {!compact && (
        <div>
          <label className="block text-sm font-medium text-forest mb-2" htmlFor="message">Message (facultatif)</label>
          <textarea
            id="message"
            name="message"
            rows={4}
            data-testid="form-message"
            placeholder="Décrivez votre situation ou vos questions…"
            className="w-full bg-white border border-soft rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40 focus:border-terracotta transition-all resize-none"
          />
        </div>
      )}

      <input type="checkbox" name="botcheck" className="hidden" tabIndex="-1" autoComplete="off" />

      {status === "error" && (
        <div data-testid="form-error" className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="form-submit"
        className="w-full bg-terracotta hover:bg-[#1E3F73] disabled:opacity-60 text-white rounded-full px-8 py-4 font-outfit font-medium shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
      >
        {status === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…
          </>
        ) : (
          "Recevoir mon devis gratuit"
        )}
      </button>

      <p className="text-xs text-forest/50 text-center leading-relaxed">
        Vos informations restent strictement confidentielles. Aucun engagement. Réponse sous 24h ouvrées.
      </p>
    </form>
  );
}
